import { createMemo, For, type JSX, Show } from 'solid-js';
import { ScrollPort } from './ScrollPort';

// THE data table. Every `<table>` this app paints is this component, so the
// a11y shape and the canvas voice live in ONE place: `data-table-usage.test.ts`
// refuses a `<table` anywhere else (one named exemption, with its reason).
//
// What it guarantees, for every call site at once:
// - a keyboard-reachable, named horizontal scroll stop (`<ScrollPort/>`);
// - the `<table>` itself named with the same `label` — the port is a `region`
//   and names the scroll stop, not the grid, so without this the table is
//   anonymous to a screen reader and to `getByRole('table', { name })`;
// - `scope="col"` on every header and `scope="row"` on a `rowHeader` column;
// - an sr-only header for a column whose header would be visual noise (row
//   actions) — the header cell still exists, so the column is still named;
// - `data-key` on every row, from `rowKey`, as the one stable row address — and the
//   row's DOM identity: a refresh with new row objects under the same keys reuses the
//   rows, repainting only a row whose data changed (`rowSlots`, `sameData`);
// - on a column that opts in with `sort` (#6561): `aria-sort` on its `<th>` (the
//   columnheader, the one role ARIA allows it on) and a native `<button>` header, so it
//   sorts by mouse and by keyboard. A table with no `sort` column renders as it did.
//
// Class contract (styled in `styles/ui/_table.scss`): the port's own class >
// `table.data-table`, `th > button.data-table__sort`, `td.data-table__mono`, `th|td.data-table__end`,
// `tr.data-table__row--live`, `tr.data-table__detail`. A cell that holds row
// buttons renders its own `div.data-table__actions` (it may also hold an inline
// error under them).
//
// Presenter only: rows arrive filtered and sorted, and the empty state is the
// caller's (`list-empty` / `<EmptyState/>`), which knows what "none" means. A sortable
// header only SAYS how the rows are sorted and ASKS for another order (`onSort`); the
// caller sorts.

export type DataTableColumn<R> = {
  /** Stable identity of the column — unique within one table (asserted). */
  readonly key: string;
  /** Already translated. */
  readonly header: string;
  readonly cell: (row: R) => JSX.Element;
  readonly align?: 'start' | 'end';
  /** Monospace cell (ids, names, counts). */
  readonly mono?: boolean;
  /** The header is announced but not painted (e.g. a row-actions column). */
  readonly srOnlyHeader?: boolean;
  /** Cells render as `<th scope="row">` — the column that names each row. */
  readonly rowHeader?: boolean;
  /** A view's hook on this column's header AND cells (e.g. hide below `sm`). */
  readonly class?: string;
  /**
   * Opt in to a sortable header: `state` reads the column's `aria-sort` now, and a press on
   * the header button calls `onSort` (the caller decides the next order). `state` is a
   * getter so a new order repaints `aria-sort` without new column objects: the CALLER keeps
   * its column objects stable across renders, or `<For>` replaces the header cell and the
   * pressed button loses keyboard focus.
   */
  readonly sort?: {
    readonly state: () => 'ascending' | 'descending' | 'none';
    readonly onSort: () => void;
  };
};

export type DataTableProps<R> = {
  /** Names both the scroll stop and the table. Already translated. */
  readonly label: string;
  readonly columns: readonly DataTableColumn<R>[];
  readonly rows: readonly R[];
  readonly rowKey: (row: R) => string;
  /** `live` marks a row whose work is running right now. */
  readonly rowTone?: (row: R) => 'live' | undefined;
  /** `aria-current` on the selected row of a master/detail list. */
  readonly rowCurrent?: (row: R) => boolean;
  /** A full-width row under a row while `open` — an expansion, not a column. */
  readonly rowDetail?: {
    readonly open: (row: R) => boolean;
    readonly render: (row: R) => JSX.Element;
  };
  /** A view's hook on the `<table>`. */
  readonly class?: string;
};

function uniqueKeys<C extends { readonly key: string }>(columns: readonly C[]): readonly C[] {
  const seen = new Set<string>();
  for (const c of columns) {
    if (seen.has(c.key)) {
      throw new Error(`DataTable: duplicate column key "${c.key}" — column keys must be unique`);
    }
    seen.add(c.key);
  }
  return columns;
}

function cellClass<R>(column: DataTableColumn<R>, header: boolean): string | undefined {
  const parts = [
    !header && column.mono ? 'data-table__mono' : '',
    column.align === 'end' ? 'data-table__end' : '',
    column.class ?? '',
  ].filter((p) => p !== '');
  return parts.length > 0 ? parts.join(' ') : undefined;
}

/**
 * Each row's slot: its `rowKey`, with a second, third… row under the same key told
 * apart by its occurrence. `<For>` over these strings keeps a row's DOM across a
 * refresh that replaces the row OBJECTS (a live view's backfill → stream swap, a
 * re-read) — keyed by reference, every such refresh rebuilt every row and a focused
 * row button fell out of the document, dropping keyboard focus to `<body>`.
 */
function rowSlots<R>(
  rows: readonly R[],
  rowKey: (row: R) => string,
): { readonly order: readonly string[]; readonly rows: ReadonlyMap<string, R> } {
  const seen = new Map<string, number>();
  const order: string[] = [];
  const bySlot = new Map<string, R>();
  for (const row of rows) {
    const key = rowKey(row);
    const n = seen.get(key) ?? 0;
    seen.set(key, n + 1);
    const slot = n === 0 ? key : `${key}\u0000${n}`;
    order.push(slot);
    bySlot.set(slot, row);
  }
  return { order, rows: bySlot };
}

/**
 * Structural equality over wire-shaped data: plain objects and arrays compare by
 * content, a `Date` by its instant, anything else by `Object.is` — so a `Map`,
 * class instance or function that is a new object counts as changed and repaints.
 */
function sameData(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => sameData(v, b[i]));
  }
  if (!isPlainObject(a) || !isPlainObject(b)) return false;
  const keys = Object.keys(a);
  return (
    keys.length === Object.keys(b).length &&
    keys.every((k) => Object.hasOwn(b, k) && sameData(a[k], b[k]))
  );
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  if (typeof v !== 'object' || v === null) return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

export function DataTable<R>(props: DataTableProps<R>): JSX.Element {
  const columns = createMemo(() => uniqueKeys(props.columns));
  const slots = createMemo(() => rowSlots(props.rows, props.rowKey));
  const tableClass = (): string => (props.class ? `data-table ${props.class}` : 'data-table');
  return (
    <ScrollPort label={props.label}>
      <table class={tableClass()} aria-label={props.label}>
        <thead>
          <tr>
            <For each={columns()}>
              {(column) => (
                <th scope="col" class={cellClass(column, true)} aria-sort={column.sort?.state()}>
                  <Show
                    when={column.sort}
                    fallback={
                      <Show when={column.srOnlyHeader} fallback={column.header}>
                        <span class="sr-only">{column.header}</span>
                      </Show>
                    }
                  >
                    {(sort) => (
                      <button
                        type="button"
                        class="data-table__sort"
                        data-sort-key={column.key}
                        onClick={() => sort().onSort()}
                      >
                        {column.header}
                      </button>
                    )}
                  </Show>
                </th>
              )}
            </For>
          </tr>
        </thead>
        <tbody>
          <For each={slots().order}>
            {(slot) => {
              // The newest row under this slot, kept when it is structurally equal to
              // the one painted: a refreshed-but-unchanged row repaints nothing.
              const row = createMemo(() => slots().rows.get(slot) as R, undefined, {
                equals: sameData,
              });
              return (
                <>
                  <tr
                    data-key={props.rowKey(row())}
                    classList={{ 'data-table__row--live': props.rowTone?.(row()) === 'live' }}
                    aria-current={props.rowCurrent?.(row()) ? 'true' : undefined}
                  >
                    <For each={columns()}>
                      {(column) =>
                        column.rowHeader ? (
                          <th scope="row" class={cellClass(column, false)}>
                            {column.cell(row())}
                          </th>
                        ) : (
                          <td class={cellClass(column, false)}>{column.cell(row())}</td>
                        )
                      }
                    </For>
                  </tr>
                  <Show when={props.rowDetail?.open(row())}>
                    <tr class="data-table__detail">
                      <td colspan={columns().length}>{props.rowDetail?.render(row())}</td>
                    </tr>
                  </Show>
                </>
              );
            }}
          </For>
        </tbody>
      </table>
    </ScrollPort>
  );
}
